import { Bell, Menu, Search, User2 } from 'lucide-react'
import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/hooks'
import { format } from 'date-fns'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

const titleMap: Record<string, { title: string; subtitle: string }> = {
  '/': {
    title: 'Overview',
    subtitle: 'Ringkasan operasional aplikasi, user, dan Firebase.',
  },
  '/users': {
    title: 'Users & Profiles',
    subtitle: 'Kelola user, admin role, profil, dan sinkronisasi data.',
  },
  '/analytics': {
    title: 'Analytics',
    subtitle: 'Pantau MAU, aktivitas kesehatan, workout, dan engagement.',
  },
  '/content': {
    title: 'Artikel & Kelas',
    subtitle: 'Kelola artikel, kelas, banner, dan aset konten utama Parlent.',
  },
  '/commerce': {
    title: 'Katalog Produk',
    subtitle: 'Kategori, produk, dan katalog utama Parlent.',
  },
  '/leaderboard': {
    title: 'Leaderboard',
    subtitle: 'Performa user, engagement score, dan ranking aplikasi.',
  },
  '/support': {
    title: 'Support',
    subtitle: 'Ticketing, FAQ, bug report, dan operasional help center.',
  },
  '/app-settings': {
    title: 'Settings',
    subtitle: 'App version, banner, feature flags, dan konfigurasi global.',
  },
  '/profile': {
    title: 'My Profile',
    subtitle: 'Kelola profil, foto, dan preferensi akun admin Anda.',
  },
}

interface HeaderProps {
  onMenuClick?: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const location = useLocation()
  const { adminProfile, user } = useAuth()
  const meta = titleMap[location.pathname] ?? titleMap['/']

  return (
    <header className="sticky top-0 z-30 border-b border-stone-800 bg-[#0A0A0A]/70 backdrop-blur">
      <div className="flex items-center justify-between gap-4 px-4 py-4 md:px-6">
        <div className="flex min-w-0 items-center gap-4">
          <button
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-stone-800 bg-[#141414] text-stone-200 shadow-sm lg:hidden"
            onClick={onMenuClick}
            type="button"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-orange-500">
              {format(new Date(), 'EEEE, MMMM do, yyyy')}
            </p>
            <h1 className="truncate text-2xl font-semibold text-white">
              {meta.title}
            </h1>
            <p className="truncate text-sm text-stone-400">{meta.subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-4">
          <div className="hidden items-center rounded-2xl border border-stone-800 bg-[#141414] px-3 py-2 md:flex">
            <Search className="mr-2 h-4 w-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search..."
              className="w-56 bg-transparent text-sm text-stone-200 outline-none"
            />
          </div>

          <button
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-stone-800 bg-[#141414] text-stone-200 shadow-sm"
            type="button"
          >
            <Bell className="h-5 w-5" />
            <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-orange-600" />
          </button>

          <Link 
            to="/profile"
            className="flex items-center gap-3 rounded-2xl border border-stone-800 bg-[#141414] px-3 py-2 shadow-sm hover:border-orange-500/50 hover:bg-stone-800/50 transition-all cursor-pointer group"
          >
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-white group-hover:text-orange-400 transition-colors">
                {user?.displayName || 'User'}
              </p>
              <p className="text-xs text-stone-400">
                {adminProfile?.role ?? 'viewer'}
              </p>
            </div>
            <Avatar className="h-10 w-10 border-2 border-stone-800 shadow-md group-hover:border-orange-500/50 transition-all">
              <AvatarImage src={user?.photoURL || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-stone-800 to-stone-900 text-stone-500">
                <User2 className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </div>
    </header>
  )
}
